import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import type { EmojiClickData } from 'emoji-picker-react';
import { BellRing, Check, Copy, Menu, MessageSquare, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDmTyping } from '@/hooks/useDmTyping';
import { useDmVoiceCall } from '@/hooks/useDmVoiceCall';
import { useUserMessagesContext } from '@/contexts/UserMessagesContext';
import type { PresenceStatus, UserConnection, UserMessage } from '@/hooks/useUserMessages';
import ContactsList from '@/components/messenger/ContactsList';
import MessageInputDM, { type MessageInputDMHandle } from '@/components/messenger/MessageInputDM';
import MessengerActionToolbar from '@/components/messenger/MessengerActionToolbar';
import MessengerSidebarTools, { type MessengerLookupResult } from '@/components/messenger/MessengerSidebarTools';
import MessageAttachments from '@/components/messenger/MessageAttachments';
import MessageReactions from '@/components/messenger/MessageReactions';
import StatusMenu from '@/components/messenger/StatusMenu';
import ChatMessageContent from '@/components/lobby/ChatMessageContent';
import EmojiPickerPopover from '@/components/lobby/EmojiPickerPopover';
import {
  MESSENGER_ACTIVITY_OPTIONS,
  MESSENGER_GAME_OPTIONS,
  type MessengerAppInvitePayload,
  type MessengerInviteCategory,
  type MessengerInviteOption,
  parseMessengerAction,
  serializeMessengerAction,
} from '@/components/messenger/actionPayload';
import { cn } from '@/lib/utils';

const messengerSupabase = supabase as any;

const presenceDotClassName: Record<PresenceStatus, string> = {
  online: 'bg-emerald-500',
  away: 'bg-amber-400',
  dnd: 'bg-red-500',
  offline: 'bg-slate-400',
};

const inviteOptionsById = [...MESSENGER_ACTIVITY_OPTIONS, ...MESSENGER_GAME_OPTIONS].reduce<
  Record<string, MessengerInviteOption>
>((accumulator, option) => {
  accumulator[option.appId] = option;
  return accumulator;
}, {});

const categoryLabel: Record<MessengerInviteCategory, string> = {
  invite: 'App',
  activity: 'Activity',
  game: 'Game',
};

const getOtherUserId = (connection: UserConnection, currentUserId: string) =>
  connection.user_id === currentUserId ? connection.connected_user_id : connection.user_id;

const getInitials = (value?: string | null) => {
  if (!value) {
    return 'U';
  }

  return value
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const getConversationMessages = (messages: UserMessage[], currentUserId: string, otherUserId: string) =>
  messages
    .filter(
      (message) =>
        (message.sender_id === currentUserId && message.recipient_id === otherUserId) ||
        (message.sender_id === otherUserId && message.recipient_id === currentUserId)
    )
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

const openMessengerProgram = (payload: MessengerAppInvitePayload) => {
  window.dispatchEvent(
    new CustomEvent('operator:openWindow', {
      detail: {
        appId: payload.appId,
        title: payload.title,
      },
    })
  );
};

function MessengerInviteMessageCard({
  payload,
  senderName,
  isOutgoing,
}: {
  payload: MessengerAppInvitePayload;
  senderName: string;
  isOutgoing: boolean;
}) {
  const option = inviteOptionsById[payload.appId];
  const Icon = option?.icon || BellRing;

  return (
    <div
      className={cn(
        'rounded-xl border p-3',
        isOutgoing
          ? 'border-border/60 bg-muted/30 text-card-foreground'
          : 'border-primary/35 bg-primary/5 text-accent-foreground'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'rounded-lg p-2',
            isOutgoing ? 'bg-primary/10 text-primary' : 'bg-primary/12 text-primary'
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold">{payload.title}</p>
            <Badge
              variant="outline"
              className={cn(
                'capitalize',
                isOutgoing
                  ? 'border-border/60 bg-background/40 text-foreground'
                  : 'border-primary/25 bg-primary/10 text-primary'
              )}
            >
              {categoryLabel[payload.category]}
            </Badge>
          </div>
          <p
            className={cn(
              'mt-1 text-xs',
              isOutgoing ? 'text-muted-foreground' : 'text-[hsl(var(--accent-foreground)/0.82)]'
            )}
          >
            {isOutgoing ? `You shared ${payload.title} in this chat.` : `${senderName} shared ${payload.title} in this chat.`}
          </p>
          <p className={cn('mt-2 text-sm', isOutgoing ? 'text-card-foreground' : 'text-accent-foreground')}>
            {payload.description}
          </p>
          {payload.note ? (
            <p
              className={cn(
                'mt-2 text-xs italic',
                isOutgoing ? 'text-muted-foreground' : 'text-[hsl(var(--accent-foreground)/0.86)]'
              )}
            >
              "{payload.note}"
            </p>
          ) : null}
          <div className="mt-3">
            <Button
              size="sm"
              variant={isOutgoing ? 'outline' : 'default'}
              className={cn(isOutgoing && 'border-border/60 bg-background hover:bg-muted/50')}
              onClick={() => openMessengerProgram(payload)}
            >
              Open {payload.title}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Messenger({
  onOpenWindow,
}: {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
}) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationScrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<MessageInputDMHandle>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRailOpen, setIsRailOpen] = useState(!isMobile);
  const [ownMessengerId, setOwnMessengerId] = useState<string | null>(null);
  const [ownMessengerIdLoading, setOwnMessengerIdLoading] = useState(true);
  const [activeFriendMessengerId, setActiveFriendMessengerId] = useState<string | null>(null);
  const [activeFriendIdCopied, setActiveFriendIdCopied] = useState(false);
  const {
    connections,
    messages,
    loading,
    activeConversation,
    setActiveConversation,
    markAsRead,
    sendMessage,
    toggleReaction,
    requestConnection,
    updateConnectionStatus,
    presenceMeta,
    selfPresenceStatus,
    selfPresenceMessage,
    setPresenceStatus,
  } = useUserMessagesContext();

  useEffect(() => {
    setIsRailOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    let cancelled = false;

    const ensureMessengerIdentity = async () => {
      if (!user) {
        setOwnMessengerId(null);
        setOwnMessengerIdLoading(false);
        return;
      }

      setOwnMessengerIdLoading(true);
      const { data, error } = await messengerSupabase.rpc('ensure_messenger_identity');

      if (cancelled) {
        return;
      }

      if (error) {
        console.error('[Messenger] Failed to load Messenger ID:', error);
        setOwnMessengerId(null);
      } else {
        setOwnMessengerId(data ? String(data) : null);
      }

      setOwnMessengerIdLoading(false);
    };

    void ensureMessengerIdentity();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const lookupMessengerUser = useCallback(
    async (messengerId: string): Promise<MessengerLookupResult | null> => {
      const normalizedId = Number.parseInt(messengerId, 10);

      if (!Number.isFinite(normalizedId)) {
        return null;
      }

      const { data, error } = await messengerSupabase.rpc('lookup_messenger_user', {
        p_messenger_id: normalizedId,
      });

      if (error) {
        console.error('[Messenger] Failed to look up Messenger user:', error);
        toast({
          title: 'Lookup failed',
          description: 'Messenger could not check that ID right now.',
          variant: 'destructive',
        });
        return null;
      }

      const match = data?.[0];
      if (!match) {
        return null;
      }

      return {
        userId: match.user_id,
        messengerId: String(match.messenger_id),
        displayName: match.display_name || 'User',
        avatarUrl: match.avatar_url,
      };
    },
    [toast]
  );

  const handleRequestConnection = useCallback(
    async (targetUserId: string, displayName: string) => {
      const result = await requestConnection(targetUserId);

      if (!result) {
        return false;
      }

      toast({
        title: 'Friend request sent',
        description: `A request was sent to ${displayName}.`,
      });

      return true;
    },
    [requestConnection, toast]
  );

  const acceptedConnections = useMemo(() => {
    if (!user) {
      return [] as UserConnection[];
    }

    return connections.filter((connection) => {
      if (connection.status !== 'accepted') {
        return false;
      }

      return connection.user_id === user.id || connection.connected_user_id === user.id;
    });
  }, [connections, user]);

  useEffect(() => {
    if (!acceptedConnections.length) {
      setActiveConversation(null);
      return;
    }

    const stillExists = acceptedConnections.some(
      (connection) => getOtherUserId(connection, user?.id || '') === activeConversation
    );

    if (!activeConversation || !stillExists) {
      setActiveConversation(getOtherUserId(acceptedConnections[0], user?.id || ''));
    }
  }, [acceptedConnections, activeConversation, setActiveConversation, user]);

  const filteredConnections = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return acceptedConnections;
    }

    return acceptedConnections.filter((connection) =>
      (connection.connected_user_name || 'User').toLowerCase().includes(query)
    );
  }, [acceptedConnections, searchTerm]);

  const currentConnection = useMemo(() => {
    if (!user || !activeConversation) {
      return null;
    }

    return (
      acceptedConnections.find(
        (connection) => getOtherUserId(connection, user.id) === activeConversation
      ) ?? null
    );
  }, [acceptedConnections, activeConversation, user]);

  useEffect(() => {
    let cancelled = false;

    const loadActiveFriendMessengerId = async () => {
      if (!currentConnection || !user) {
        setActiveFriendMessengerId(null);
        return;
      }

      const otherUserId = getOtherUserId(currentConnection, user.id);
      const { data, error } = await messengerSupabase.rpc('get_connected_messenger_id', {
        p_other_user_id: otherUserId,
      });

      if (cancelled) {
        return;
      }

      if (error) {
        console.error('[Messenger] Failed to load connected Messenger ID:', error);
        setActiveFriendMessengerId(null);
        return;
      }

      setActiveFriendMessengerId(data ? String(data) : null);
    };

    void loadActiveFriendMessengerId();

    return () => {
      cancelled = true;
    };
  }, [currentConnection, user]);

  useEffect(() => {
    setActiveFriendIdCopied(false);
  }, [activeFriendMessengerId]);

  const currentConversation = useMemo(() => {
    if (!user || !activeConversation) {
      return [] as UserMessage[];
    }

    return getConversationMessages(messages, user.id, activeConversation);
  }, [activeConversation, messages, user]);

  const currentPresence = activeConversation ? presenceMeta[activeConversation] : undefined;
  const currentPresenceStatus = currentPresence?.status ?? 'offline';
  const currentPresenceMessage = currentPresence?.status_message ?? '';
  const { isOtherTyping, setSelfTyping } = useDmTyping(user?.id, activeConversation);
  const {
    phase: voicePhase,
    isMuted: voiceMuted,
    error: voiceError,
    remoteAudioRef,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    clearError: clearVoiceError,
  } = useDmVoiceCall(user?.id, activeConversation);

  const toggleReactionWithoutScroll = useCallback(
    async (messageId: string, emojiData: EmojiClickData) => {
      const scrollContainer = conversationScrollRef.current;
      const previousScrollTop = scrollContainer?.scrollTop ?? 0;
      const previousScrollHeight = scrollContainer?.scrollHeight ?? 0;

      await toggleReaction(messageId, emojiData);

      requestAnimationFrame(() => {
        if (!conversationScrollRef.current) {
          return;
        }

        const nextScrollHeight = conversationScrollRef.current.scrollHeight;
        const scrollDelta = nextScrollHeight - previousScrollHeight;
        conversationScrollRef.current.scrollTop = previousScrollTop + scrollDelta;
      });
    },
    [toggleReaction]
  );

  useEffect(() => {
    if (!user || !activeConversation) {
      return;
    }

    currentConversation
      .filter((message) => message.sender_id === activeConversation && message.recipient_id === user.id && !message.read)
      .forEach((message) => {
        void markAsRead(message.id);
      });
  }, [activeConversation, currentConversation, markAsRead, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConversation, currentConversation.length, isOtherTyping, voicePhase]);

  const currentUserName = profile?.name || user?.email?.split('@')[0] || 'You';
  const currentUserAvatar = profile?.avatar;
  const recipientName = currentConnection?.connected_user_name || 'Conversation';
  const recipientAvatar = currentConnection?.connected_user_avatar;

  const copyActiveFriendMessengerId = useCallback(async () => {
    if (!activeFriendMessengerId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(activeFriendMessengerId);
      setActiveFriendIdCopied(true);
      window.setTimeout(() => setActiveFriendIdCopied(false), 1400);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Please copy the Messenger ID manually.',
        variant: 'destructive',
      });
    }
  }, [activeFriendMessengerId, toast]);

  const handleSendInvite = async (option: MessengerInviteOption, mode: MessengerInviteCategory, note?: string) => {
    if (!currentConnection || !user) {
      return;
    }

    const payload: MessengerAppInvitePayload = {
      version: 1,
      type: 'app-invite',
      category: mode,
      appId: option.appId,
      title: option.title,
      description: option.description,
      note,
    };

    const result = await sendMessage(
      getOtherUserId(currentConnection, user.id),
      serializeMessengerAction(payload)
    );

    if (!result) {
      toast({
        title: 'Share failed',
        description: 'The app share could not be sent right now.',
        variant: 'destructive',
      });
    }
  };

  const handleVoiceAction = async () => {
    if (voicePhase === 'active' || voicePhase === 'outgoing' || voicePhase === 'connecting') {
      await endCall();
      return;
    }

    if (voicePhase === 'incoming') {
      await acceptCall();
      return;
    }

    await startCall();
  };

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-sm font-medium text-foreground">Sign in to use Messenger</p>
          <p className="mt-1 text-xs text-muted-foreground">Your accepted connections and direct messages will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 bg-background">
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      <aside
        className={cn(
          'flex h-full min-h-0 w-full max-w-80 shrink-0 flex-col border-r border-border/70 bg-muted/20 p-3 transition-all duration-200 md:w-80',
          isMobile && !isRailOpen && 'hidden',
          !isMobile && !isRailOpen && 'w-20 max-w-20 p-2'
        )}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className={cn('min-w-0 flex-1', !isRailOpen && !isMobile && 'hidden')}>
            <StatusMenu
              currentUserName={currentUserName}
              currentUserAvatar={currentUserAvatar}
              messengerId={ownMessengerId}
              messengerIdLoading={ownMessengerIdLoading}
              status={selfPresenceStatus}
              statusMessage={selfPresenceMessage}
              onChange={setPresenceStatus}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => setIsRailOpen((current) => !current)}
            aria-label={isRailOpen ? 'Collapse conversation rail' : 'Expand conversation rail'}
          >
            {isMobile ? <PanelLeftClose className="h-5 w-5" /> : isRailOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
          </Button>
        </div>

        {!isRailOpen && !isMobile ? (
          <div className="flex flex-1 flex-col items-center justify-start gap-3 pt-2">
            {filteredConnections.slice(0, 6).map((connection) => {
              const otherUserId = getOtherUserId(connection, user.id);
              return (
                <button
                  key={otherUserId}
                  type="button"
                  onClick={() => setActiveConversation(otherUserId)}
                  className={cn(
                    'relative rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-ring',
                    activeConversation === otherUserId && 'ring-2 ring-primary'
                  )}
                  aria-label={connection.connected_user_name || 'Conversation'}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={connection.connected_user_avatar} alt={connection.connected_user_name || 'Conversation'} />
                    <AvatarFallback>{getInitials(connection.connected_user_name)}</AvatarFallback>
                  </Avatar>
                </button>
              );
            })}
          </div>
        ) : (
          <>
            <MessengerSidebarTools
              currentUserId={user.id}
              connections={connections}
              onLookupUser={lookupMessengerUser}
              onRequestConnection={handleRequestConnection}
              onUpdateConnectionStatus={updateConnectionStatus}
            />
            <div className="mb-3">
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search conversations"
                aria-label="Search conversations"
              />
            </div>
            <ScrollArea className="flex-1 pr-1">
              <ContactsList
                currentUserId={user.id}
                connections={filteredConnections}
                messages={messages}
                presenceMeta={presenceMeta}
                selectedUserId={activeConversation}
                onSelect={(selectedUserId) => {
                  setActiveConversation(selectedUserId);
                  if (isMobile) {
                    setIsRailOpen(false);
                  }
                }}
              />
            </ScrollArea>
          </>
        )}
      </aside>

      <section className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
          {currentConnection ? (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="h-11 w-11 border border-border/60">
                <AvatarImage src={recipientAvatar} alt={recipientName} />
                <AvatarFallback>{getInitials(recipientName)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold text-foreground">{recipientName}</p>
                  <span
                    className={cn('h-2.5 w-2.5 rounded-full', presenceDotClassName[currentPresenceStatus])}
                    aria-label={`Presence: ${currentPresenceStatus}`}
                  />
                  {activeFriendMessengerId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={copyActiveFriendMessengerId}
                      aria-label={`Copy ${recipientName}'s Messenger ID`}
                      title={`Copy ${recipientName}'s Messenger ID (${activeFriendMessengerId})`}
                    >
                      {activeFriendIdCopied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  )}

                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {activeFriendMessengerId ? `ID ${activeFriendMessengerId} · ` : ''}
                  {currentPresenceMessage || (currentPresenceStatus === 'offline' ? 'Offline' : currentPresenceStatus.replace('dnd', 'Do Not Disturb'))}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm font-semibold text-foreground">Messenger</p>
              <p className="text-xs text-muted-foreground">Choose an accepted connection to start chatting.</p>
            </div>
          )}

          {isMobile && (
            <Button type="button" variant="outline" size="icon" onClick={() => setIsRailOpen(true)} aria-label="Open conversations">
              <Menu className="h-4 w-4" />
            </Button>
          )}
        </div>

        {currentConnection && (
          <MessengerActionToolbar
            onOpenFiles={() => composerRef.current?.openFilePicker()}
            onVoiceAction={handleVoiceAction}
            onVoiceToggleMute={toggleMute}
            onSendInvite={handleSendInvite}
            voicePhase={voicePhase}
            voiceMuted={voiceMuted}
          />
        )}

        <div className="space-y-2 border-b border-border/40 bg-background px-4 py-2">
          {voiceError && (
            <div className="flex items-center justify-between rounded-lg border border-amber-300/40 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <span>{voiceError}</span>
              <Button variant="ghost" size="sm" onClick={clearVoiceError}>
                Dismiss
              </Button>
            </div>
          )}

          {voicePhase === 'incoming' && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
              <div>
                <p className="font-medium">Incoming voice call</p>
                <p className="text-xs text-muted-foreground">{recipientName} wants to talk.</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={declineCall}>
                  Decline
                </Button>
                <Button size="sm" onClick={acceptCall}>
                  Accept
                </Button>
              </div>
            </div>
          )}

          {(voicePhase === 'outgoing' || voicePhase === 'connecting' || voicePhase === 'active') && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
              <div>
                <p className="font-medium">
                  {voicePhase === 'active' ? 'Voice call active' : voicePhase === 'connecting' ? 'Connecting voice call…' : 'Calling…'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {voicePhase === 'active' ? 'You are connected in voice chat.' : `Trying to reach ${recipientName}.`}
                </p>
              </div>
              <div className="flex gap-2">
                {voicePhase === 'active' && (
                  <Button size="sm" variant="outline" onClick={toggleMute}>
                    {voiceMuted ? 'Unmute' : 'Mute'}
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={endCall}>
                  End call
                </Button>
              </div>
            </div>
          )}
        </div>

        <ScrollArea ref={conversationScrollRef} className="flex-1 bg-background px-4 py-4">
          {!currentConnection ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center text-muted-foreground">
              <MessageSquare className="mb-3 h-12 w-12 opacity-20" />
              <p className="text-sm font-medium text-foreground">No conversation selected</p>
              <p className="mt-1 max-w-sm text-xs">Pick a conversation from the left rail to open a direct message window.</p>
            </div>
          ) : loading ? (
            <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-muted-foreground">
              Loading conversation…
            </div>
          ) : currentConversation.length === 0 ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center text-muted-foreground">
              <MessageSquare className="mb-3 h-12 w-12 opacity-20" />
              <p className="text-sm font-medium text-foreground">No messages yet</p>
              <p className="mt-1 max-w-sm text-xs">Send the first message to start your conversation with {recipientName}.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentConversation.map((message) => {
                const isOutgoing = message.sender_id === user.id;
                const actionPayload = parseMessengerAction(message.content);
                return (
                  <div key={message.id} className={cn('flex gap-3', isOutgoing ? 'justify-end' : 'justify-start')}>
                    {!isOutgoing && (
                      <Avatar className="mt-1 h-8 w-8 shrink-0 border border-border/50">
                        <AvatarImage src={recipientAvatar} alt={recipientName} />
                        <AvatarFallback>{getInitials(recipientName)}</AvatarFallback>
                      </Avatar>
                    )}

                    <div className={cn('group flex max-w-[85%] flex-col', isOutgoing ? 'items-end' : 'items-start')}>
                      <div
                        className={cn(
                          'rounded-2xl border px-4 py-3 shadow-sm',
                          isOutgoing
                            ? 'border-border/70 bg-card text-card-foreground'
                            : 'border-primary/70 bg-accent/60 text-accent-foreground shadow-[0_0_0_1px_hsl(var(--primary)/0.08)]'
                        )}
                      >
                        {actionPayload ? (
                          <MessengerInviteMessageCard
                            payload={actionPayload}
                            senderName={isOutgoing ? currentUserName : recipientName}
                            isOutgoing={isOutgoing}
                          />
                        ) : (
                          <div className="space-y-3">
                            <MessageAttachments
                              attachments={message.attachments}
                              isOutgoing={isOutgoing}
                              onOpenWindow={onOpenWindow}
                            />
                            {message.content.trim() ? (
                              <div
                                className={cn(
                                  'text-sm',
                                  isOutgoing
                                    ? '[&_a]:text-primary [&_a]:underline [&_p]:text-card-foreground'
                                    : '[&_a]:text-accent-foreground [&_a]:underline [&_p]:text-accent-foreground'
                                )}
                              >
                                <ChatMessageContent content={message.content} channelMembers={[]} />
                              </div>
                            ) : null}
                          </div>
                        )}
                        <div
                          className={cn(
                            'mt-2 text-[11px]',
                            isOutgoing ? 'text-muted-foreground' : 'text-[hsl(var(--accent-foreground)/0.78)]'
                          )}
                        >
                          {format(new Date(message.created_at), 'PP p')}
                        </div>
                      </div>

                      <div className={cn('mt-1 flex w-full flex-wrap items-center gap-1.5', isOutgoing ? 'justify-end' : 'justify-start')}>
                        <MessageReactions
                          reactions={message.reactions}
                          onToggleReaction={(emoji, imageUrl) =>
                            void toggleReactionWithoutScroll(message.id, { emoji, imageUrl: imageUrl || '' } as EmojiClickData)
                          }
                        />

                        <EmojiPickerPopover
                          triggerClassName="h-7 w-7 rounded-full border border-border/60 bg-background/80 text-muted-foreground opacity-70 shadow-sm transition-all hover:bg-accent hover:text-accent-foreground hover:opacity-100 group-hover:opacity-100 focus-visible:opacity-100"
                          onEmojiSelect={(emojiData) => void toggleReactionWithoutScroll(message.id, emojiData)}
                        />
                      </div>
                    </div>

                    {isOutgoing && (
                      <Avatar className="mt-1 h-8 w-8 shrink-0 border border-border/50">
                        <AvatarImage src={currentUserAvatar} alt={currentUserName} />
                        <AvatarFallback>{getInitials(currentUserName)}</AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {currentConnection && (
          <div className="border-t border-border/70 bg-card/70 px-2.5 pb-2.5 pt-1.5">
            {isOtherTyping ? (
              <div className="px-1 pb-1 text-[11px] text-muted-foreground">
                {recipientName} is typing…
              </div>
            ) : null}
            <MessageInputDM
              ref={composerRef}
              recipientId={activeConversation || ''}
              setSelfTyping={setSelfTyping}
            />
          </div>
        )}
      </section>
    </div>
  );
}